import { useState, useEffect } from 'react'
import Loading from '@/components/Loading'
import { useQuery } from '@tanstack/react-query'
import axios from 'axios'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { routes } from '@/routes/utils'
import { useTranslation } from "react-i18next"
import { clearAllCookies } from '@/utils/auth'
import { capture } from '@/utils/analytics'
import { identifyUserInPostHog } from '@/utils/posthog-identification'
import { useQueryGraphQL } from '@/use-graphql'
import { currentUserInfoQuery } from './graphql/query'

export function AzureLoginCallback() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const code = searchParams.get('code')
  const isRegistration = searchParams.get('is_registration') === 'true'
  const [error, setError] = useState<string | null>(null)
  const [authSuccess, setAuthSuccess] = useState(false)

  // Fetch current user data after successful SSO authentication
  const { data: userData } = useQueryGraphQL(
    ['current-user-sso'],
    currentUserInfoQuery,
    {},
    {
      enabled: authSuccess,
    }
  )

  const { data, isLoading, isError } = useQuery(
    ['azure-callback', code],
    async () => {
      try {
        // Make sure axios is configured to send cookies
        const response = await axios.get(
          `${import.meta.env.VITE_BASE_URL}/api/auth/azure-callback/?code=${code}`,
          {
            withCredentials: true, // This is critical for session cookies
          },
        )
        return response.data
      } catch (err: any) {
        console.log('Error during Azure login callback:', err)
        setError(err?.response?.data?.error || 'An error occurred during authentication')

        throw err
      }
    },
    {
      enabled: !!code,
      retry: false,
    },
  )

  useEffect(() => {
    if (data) {
      localStorage.setItem('org-type', 'company')
      localStorage.removeItem('failed-login-with-microsoft')
      setAuthSuccess(true)
    }
  }, [data])

  useEffect(() => {
    if (userData?.me) {
      const user = userData.me
      
      // FR-05, FR-06, FR-07: User & Organization Identification
      // Must be called BEFORE user_registered to ensure survey targeting works
      identifyUserInPostHog(user)
      
      // Track user_registered event if this is a registration flow
      if (isRegistration) {
        const registrationContextStr = localStorage.getItem('sso_registration_context')
        if (registrationContextStr) {
          try {
            const registrationContext = JSON.parse(registrationContextStr)
            capture('user_registered', {
              registration_method: 'sso',
              ...registrationContext,
            })
            // Clear registration context after use
            localStorage.removeItem('sso_registration_context')
          } catch (e) {
            console.error('Failed to parse SSO registration context:', e)
          }
        }
      }
      
      // Track successful SSO login event using session_count from backend (FR-15)
      capture('user_logged_in', { 
        session_number: user?.sessionCount || 1,
        login_method: 'sso'
      })
      
      // Redirect to home or dashboard
      navigate('/')
    }
  }, [userData, navigate, isRegistration])

  useEffect(() => {
    setTimeout(() => {
      if (!code) {
        navigate(routes.requestCode.getPath())
      }
    }, 1000)
  }, [code, navigate])

  // Function to clear cookies and redirect
  const handleBackToLogin = () => {
    localStorage.setItem('failed-login-with-microsoft', 'true')
    
    // Clear all browser cookies to ensure clean login state
    clearAllCookies()
    
    // Redirect to login page
    navigate(routes.requestCode.getPath())
  }

  if (isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <Loading />
      </div>
    )
  }

  if (isError || error) {
    return (
      <div className="flex h-screen w-full flex-col items-center justify-center">
        <div className="text-red-500">{error ? t(error) : 'An error occurred during authentication'}</div>
        <button
          className="mt-4 rounded bg-blue-500 px-4 py-2 text-white"
          onClick={handleBackToLogin}
        >
          {t("button.back_to_login")}
        </button>
      </div>
    )
  }

  return (
    <div className="flex h-screen w-full items-center justify-center">
      <Loading />
    </div>
  )
}
